import type {
  FlagErrorReporter,
  FlagFailureKind,
  FlagTransportRoute,
} from './types';

const ABORT_PATTERNS =
  /operation was aborted|superseded by a newer context update/i;
const NETWORK_PATTERNS =
  /Failed to fetch|NetworkError when attempting to fetch|Load failed/i;

export interface ClassifiedFailure {
  kind: FlagFailureKind;
  name: string;
  message: string;
}

/**
 * The SDK's own `Failed to fetch flag configuration:` means it got a response and
 * Datadog rejected it. That wording contains "Failed to fetch", so it has to be
 * tested before the network patterns.
 */
export const classifyFailure = (error: unknown): ClassifiedFailure => {
  const asError = error instanceof Error ? error : new Error(String(error));
  const { name, message } = asError;

  let kind: FlagFailureKind = 'unknown';
  if (/^Failed to fetch flag configuration:/i.test(message)) {
    kind = 'http-error';
  } else if (name === 'AbortError' || ABORT_PATTERNS.test(message)) {
    kind = 'aborted';
  } else if (NETWORK_PATTERNS.test(message)) {
    kind = 'network-unreachable';
  }

  return { kind, name, message };
};

// Tracked as it happens — by report time the page is already gone.
let isPageUnloading = false;
if (typeof window !== 'undefined') {
  const markUnloading = () => {
    isPageUnloading = true;
  };
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('beforeunload', markUnloading);
}

/** Test seam — the listeners above are one-way for the life of a real page. */
export const setPageUnloadingForTest = (value: boolean) => {
  isPageUnloading = value;
};

/**
 * What the opaque `Failed to fetch` hides: offline, backgrounded, or unloading.
 */
const getClientContext = () => {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: { effectiveType?: string } })
      : undefined;
  return {
    onLine: nav?.onLine,
    effectiveType: nav?.connection?.effectiveType,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    isPageUnloading,
  };
};

export interface FlagInitFailure {
  route: FlagTransportRoute;
  error: unknown;
  /** Total tries, including the initial one. */
  attempts: number;
  elapsedMs: number;
  /** Host-supplied reporter. Absent means the host opted out of reporting. */
  onError?: FlagErrorReporter;
}

/**
 * Reports a config fetch that stayed broken across every retry.
 *
 * Route and kind go in the title, fingerprint and tags rather than `extra`: they split
 * the causes into separate issues, and reporters typically index tags but not `extra`.
 *
 * @returns whether it reported.
 */
export const reportFlagInitFailure = ({
  route,
  error,
  attempts,
  elapsedMs,
  onError,
}: FlagInitFailure): boolean => {
  const { kind, name, message } = classifyFailure(error);
  const client = getClientContext();

  // Not defects: an abort supersedes the request, and an unloading page cancelled it.
  if (kind === 'aborted' || client.isPageUnloading) {
    return false;
  }

  if (!onError) {
    return false;
  }

  onError(
    new Error(`[datadog-flags] config fetch failed (${route}, ${kind})`),
    {
      attempts,
      elapsedMs,
      error: { name, message },
      client,
    },
    ['datadog-flags-init', route, kind],
    {
      'flags.route': route,
      'flags.failure_kind': kind,
      'flags.attempts': String(attempts),
      'net.online': String(client.onLine ?? 'unknown'),
      'net.effective_type': String(client.effectiveType ?? 'unknown'),
      'page.visibility': String(client.visibilityState ?? 'unknown'),
      'page.unloading': String(client.isPageUnloading),
    },
  );
  return true;
};
